import * as XLSX from "xlsx-js-style";
import { MONTHS } from "../catalog-planner/utils";
import { gridLines } from "./forecastTree";

/**
 * Plan vs forecast as a sheet: one block per node (product or SKU), stacked
 * downwards — each block is 10 rows, so the 1st starts on row 1, the 2nd on
 * row 11, the 3rd on row 21 … Months run across, as on the page:
 *
 *   row 1   |              Product A (merged, centred)               |
 *   row 2   | Measure | Jan | Feb | … | Dec | YTD | Year |
 *   row 3   | Plan $  |  …
 *   …       | Actual $, Forecast $, Δ vs plan $, Plan units … Δ units
 *   row 11  |              Product B …
 *
 * Closed months: actuals; open months: forecast (same numbers as the page).
 */
export const METRICS = [
  { label: "Plan $", unit: "", key: "plan", money: true },
  { label: "Actual $", unit: "", key: "actual", money: true },
  { label: "Forecast $", unit: "", key: "forecast", money: true },
  { label: "Δ vs plan $", unit: "", key: "delta", money: true },
  { label: "Plan units", unit: "_units", key: "plan" },
  { label: "Actual units", unit: "_units", key: "actual" },
  { label: "Forecast units", unit: "_units", key: "forecast" },
  { label: "Δ units", unit: "_units", key: "delta" },
];
export const PERIODS = [...MONTHS, "YTD", "Year"];

const round = (v, money) =>
  v == null || Number.isNaN(Number(v)) ? null : money ? Math.round(v * 100) / 100 : Math.round(v);

// YTD and Year cells per metric (blank where the page shows "–").
const ytdOf = (lines, key) => (key === "forecast" ? null : lines.ytd[key] ?? null);
const yearOf = (lines, key) => (key === "actual" ? null : lines.year[key] ?? null);

export const BLOCK_ROWS = 2 + METRICS.length; // name + header + one row per measure (10)

/** → { aoa, merges } for `nodes` ({ name, plan, actual, forecast, …_units }). */
export const buildBlocks = (nodes, closed) => {
  const width = 1 + PERIODS.length; // measure label + periods
  const aoa = [];
  const merges = [];
  nodes.forEach((node) => {
    const r0 = aoa.length;
    aoa.push([node.name, ...Array(width - 1).fill("")]);
    merges.push({ s: { r: r0, c: 0 }, e: { r: r0, c: width - 1 } });
    aoa.push(["Measure", ...PERIODS]);
    const money = gridLines(node, closed, "");
    const units = gridLines(node, closed, "_units");
    METRICS.forEach((m) => {
      const lines = m.unit ? units : money;
      aoa.push([
        m.label,
        ...lines[m.key].map((v) => round(v, m.money)),
        round(ytdOf(lines, m.key), m.money),
        round(yearOf(lines, m.key), m.money),
      ]);
    });
  });
  return { aoa, merges };
};

const border = { style: "thin", color: { rgb: "D0D7DE" } };
const borders = { top: border, bottom: border, left: border, right: border };
const STYLE = {
  name: { font: { bold: true, sz: 12 }, alignment: { horizontal: "center", vertical: "center" }, fill: { fgColor: { rgb: "DCE9F9" } }, border: borders },
  head: { font: { bold: true }, alignment: { horizontal: "center", vertical: "center", wrapText: true }, fill: { fgColor: { rgb: "F2F4F7" } }, border: borders },
  month: { font: { bold: true }, border: borders },
  total: { font: { bold: true }, fill: { fgColor: { rgb: "F7F9FC" } }, border: borders },
  cell: { border: borders },
};

/** A styled worksheet of `nodes` (merged + centred names, borders, number formats). */
export const buildBlocksSheet = (nodes, closed) => {
  const { aoa, merges } = buildBlocks(nodes, closed);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!merges"] = merges;
  aoa.forEach((r, ri) => {
    const line = ri % BLOCK_ROWS; // 0 = name, 1 = header, 2… = measures
    r.forEach((_, ci) => {
      const ref = XLSX.utils.encode_cell({ r: ri, c: ci });
      if (!ws[ref]) ws[ref] = { t: "s", v: "" };
      const totalCol = ci > MONTHS.length; // YTD / Year
      if (line === 0) ws[ref].s = STYLE.name;
      else if (line === 1) ws[ref].s = STYLE.head;
      else if (ci === 0) ws[ref].s = STYLE.month;
      else {
        ws[ref].s = totalCol ? STYLE.total : STYLE.cell;
        if (ws[ref].t === "n") ws[ref].z = METRICS[line - 2]?.money ? "#,##0.00" : "#,##0";
      }
    });
  });
  ws["!cols"] = [{ wch: 14 }, ...PERIODS.map(() => ({ wch: 12 }))];
  return ws;
};

const safeName = (s) => String(s || "forecast").replace(/[^\w.-]+/g, "_").slice(0, 60);

/**
 * Download plan vs forecast for `nodes` (products, or one product's SKUs).
 * `info` rows (run, plan, …) go on a second sheet.
 */
export const downloadBlocksXlsx = (nodes, closed, { sheetName, fileName, info = [] }) => {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildBlocksSheet(nodes, closed), String(sheetName || "Forecast").slice(0, 31));
  if (info.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(info), "About");
  XLSX.writeFile(wb, `${safeName(fileName)}.xlsx`);
};
